import React, {useEffect, useState} from "react";
import {FormattedMessage, useIntl} from "react-intl";
import {useForm} from "react-hook-form";
import {AuthButton, Form, InputCheckbox, InputField, Link, useAuth} from "peggirkit";
import {registerUser} from "serpotrack-connector";
import {SetPasswordField, SetPasswordRepeatedField} from "serpotrack-frontend-authentication";

export type FormValues = {
    username: string,
    password: string,
    passwordRepeated: string,
    agreeToTerms: boolean,
};

const AuthFormRegister = () => {
    const intl = useIntl();
    const {register, getValues, handleSubmit, watch, formState: {errors}} = useForm<FormValues>();
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);
    const authContext = useAuth();

    useEffect(() => {
        if (!loading) {
            return;
        }

        const submittedData = getValues();
        const username = submittedData.username.toLowerCase();
        registerUser(username, submittedData.password).then(() => {
            // Sign in and redirect
            authContext?.onSignIn(username, submittedData.password).catch(() => {
                setLoading(false);
                setFormError(intl.formatMessage({id: "signInAfterRegisteringError"}));
            });
        }).catch(e => {
            setFormError(intl.formatMessage({
                id: e.response.status === 409 ? "emailInUse" : "errorRegistering"
            }));
            setLoading(false);
        });
    }, [loading]);

    return (
        <Form
            onSubmit={handleSubmit(() => setLoading(true))}
            error={formError}
        >
            <InputField
                id={"emailAddress"}
                displayName={intl.formatMessage({id: "emailAddress"})}
                type={"email"}
                placeholder={intl.formatMessage({id: "emailAddress.placeholder"})}
                tip={intl.formatMessage({id: "emailAddress.tip"})}
                disabled={loading}
                error={errors.username && errors.username.message}
                reactHookForm={{
                    ...register("username", {
                        required: intl.formatMessage({id: "enterEmailAddress"}),
                        minLength: {
                            value: 3,
                            message: intl.formatMessage({id: "mustContainAtLeastCharacters"}, {min: 3})
                        },
                        maxLength: {
                            value: 128,
                            message: intl.formatMessage({id: "canContainAtMostCharacters"}, {max: 128})
                        }
                    })
                }}
            />

            <SetPasswordField
                fieldName={"password"}
                displayName={intl.formatMessage({id: "password"})}
                watch={watch}
                register={register}
                disabled={loading}
                fieldError={errors.password}
            />

            <SetPasswordRepeatedField
                compareValue={watch("password")}
                register={register}
                disabled={loading}
                fieldError={errors.passwordRepeated}
            />

            <InputCheckbox
                id={"agreeToTerms"}
                displayName={
                    <>
                        <FormattedMessage id="agreeToTerms"/>
                        {" "}(
                        <Link
                            href="https://serpotrack.com/terms-and-conditions"
                            target={"_blank"}
                            text={intl.formatMessage({id: "read"})}
                        />)
                    </>
                }
                disabled={loading}
                error={errors.agreeToTerms && errors.agreeToTerms.message}
                reactHookForm={{
                    ...register("agreeToTerms", {
                        required: intl.formatMessage({id: "mustAgreeToTos"})
                    })
                }}
            />

            <AuthButton
                disabled={loading}
                loading={loading}
                text={intl.formatMessage({id: "register"})}
            />
        </Form>
    );
};

export default AuthFormRegister;